import React from 'react';
import { FolderKanban, Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

export const WorkspaceSwitcher: React.FC = () => {
  const { workspaces, currentWorkspace, loading, switchWorkspace } = useWorkspace();
  const { currentTenant } = useTenant();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const isOwnerOrAdmin = currentTenant?.role === 'OWNER' || currentTenant?.role === 'ADMIN';

  // Filter to only show active workspaces
  const activeWorkspaces = workspaces.filter(ws => ws.status !== 'ARCHIVED');

  const handleSelect = (workspaceId: string) => {
    switchWorkspace(workspaceId);
    setOpen(false);
  };

  const handleCreateNew = () => {
    setOpen(false);
    navigate('/settings?section=workspace');
  };

  if (loading || !currentWorkspace) {
    return (
      <Button variant="outline" size="sm" className="w-[180px] justify-between" disabled>
        <div className="flex items-center gap-2">
          <FolderKanban className="h-4 w-4" />
          <span className="truncate">Loading...</span>
        </div>
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Select a workspace"
          className="w-[140px] md:w-[180px] justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            <FolderKanban 
              className="h-4 w-4 shrink-0" 
              style={{ color: currentWorkspace.color || 'currentColor' }}
            />
            <span className="truncate text-xs md:text-sm">{currentWorkspace.name}</span>
          </div>
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Search workspace..." />
          <CommandList>
            <CommandEmpty>No workspace found.</CommandEmpty>
            <CommandGroup heading="Workspaces">
              {activeWorkspaces.map((workspace) => (
                <CommandItem
                  key={workspace.workspaceId}
                  value={workspace.name}
                  onSelect={() => handleSelect(workspace.workspaceId)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FolderKanban 
                      className="h-4 w-4 shrink-0" 
                      style={{ color: workspace.color || 'currentColor' }}
                    />
                    <span className="truncate">{workspace.name}</span>
                    {workspace.isDefault && (
                      <Badge variant="outline" className="ml-auto text-[10px] px-1">
                        Default
                      </Badge>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0",
                      currentWorkspace.workspaceId === workspace.workspaceId
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            {isOwnerOrAdmin && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreateNew}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Workspace
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
